"use client";

import { useSearchParams } from "next/navigation";
import { useEffect } from "react";

import { useIsClient } from "@/hooks/use-is-client";
import {
  getIndividualStateRoute,
  type IndividualStateRoute,
  type IndividualStateRouteUpperCase,
} from "@/lib/states-config";

const UTM_STORAGE_KEY = "utm-trackers";

export function getUtmTrackers(): Record<string, string> {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function getSignupContext(
  utmTrackers: Record<string, string>,
  lastViewedStateRoute: IndividualStateRoute | undefined,
): {
  signup_action: string | undefined;
  signup_state: IndividualStateRouteUpperCase | undefined;
} {
  const utmState = getIndividualStateRoute(utmTrackers.utm_signup_state);
  const signupState = utmState ?? lastViewedStateRoute;

  return {
    signup_action: utmTrackers.utm_signup_action,
    signup_state: signupState
      ? (signupState.toUpperCase() as IndividualStateRouteUpperCase)
      : undefined,
  };
}

export function buildSignupData(lastViewedStateRoute: string | undefined): {
  signup_action: string | undefined;
  signup_state: IndividualStateRouteUpperCase | undefined;
  utmTrackers: Record<string, string>;
} {
  const utmTrackers = getUtmTrackers();
  const parsedRoute = getIndividualStateRoute(lastViewedStateRoute);
  const { signup_action, signup_state } = getSignupContext(
    utmTrackers,
    parsedRoute,
  );

  const {
    utm_signup_action: _,
    utm_signup_state: __,
    ...filteredUtmTrackers
  } = utmTrackers;

  return {
    signup_action,
    signup_state,
    utmTrackers: filteredUtmTrackers,
  };
}

function getValidReferrer() {
  const referrer = document.referrer;
  const currentOrigin = window.location.origin;

  // Only store referrer if it's external and we don't already have one
  if (referrer && !referrer.startsWith(currentOrigin)) {
    return referrer;
  }

  return null;
}

function storeTrackingData(
  searchParams: ReturnType<typeof useSearchParams>,
): void {
  const utmParams: Record<string, string> = {};
  const existingUtms = getUtmTrackers();

  const validReferrer = getValidReferrer();

  if (!existingUtms.referrer && validReferrer) {
    utmParams.referrer = validReferrer;
  }

  const signupState = searchParams.get("redirect_state");

  if (signupState && typeof signupState === "string") {
    utmParams.utm_signup_state = signupState;
  }

  searchParams.forEach((value: string, key: string) => {
    const normalizedKey = key.toLowerCase();
    if (normalizedKey.startsWith("utm_")) {
      utmParams[normalizedKey] = value;
    }
  });

  if (Object.keys(utmParams).length > 0) {
    const updatedUtms = { ...existingUtms, ...utmParams };
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(updatedUtms));
  }
}

export default function UtmTrackers() {
  const isClient = useIsClient();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (isClient) {
      storeTrackingData(searchParams);
    }
  }, [searchParams, isClient]);

  return null;
}
